/* import __COLOCATED_TEMPLATE__ from './container.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import EmberObject from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { get, put } from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';
import type Router from '@ember/routing/router-service';
import type MessengerSettingsService from 'embercom/services/messenger-settings-service';
import type Store from '@ember-data/store';
//@ts-ignore no type declaration available
import PredicateGroup from 'predicates/models/predicate-group';

interface Suggestion {
  id: number;
  text: string;
  display_priority: number;
}

interface SalesMessengerConfig {
  id?: number;
  app_id?: string;
  brand_settings_id: number;
  glow_color: string | null;
  vertical_padding: number | null;
  audience_predicates: Array<Record<string, unknown>>;
  smart_suggestions_enabled: boolean;
  suggestions: Suggestion[];
}

const MIN_VERTICAL_PADDING = 20;

interface Args {
  model: $TSFixMe;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class SalesMessengerContainer extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare messengerSettingsService: MessengerSettingsService;
  @service declare store: Store;
  @service declare attributeService: $TSFixMe;

  @tracked isLoading = true;
  @tracked isSaving = false;
  @tracked config: SalesMessengerConfig | null = null;
  @tracked glowColor: string | null = null;
  @tracked verticalPadding: number | null = null;
  @tracked audiencePredicates: Array<Record<string, unknown>> = [];
  @tracked activeBrandSetting: $TSFixMe = null;
  @tracked selectedPreset = 'visitors-and-leads';
  @tracked audienceRuleset: $TSFixMe = null;
  @tracked openSectionId = 'audience';
  @tracked smartSuggestionsEnabled = false;
  @tracked suggestions: Suggestion[] = [];
  @tracked showAllSuggestions = false;

  // Snapshot of last-saved values for dirty tracking
  private savedGlowColor: string | null = null;
  private savedVerticalPadding: number | null = null;
  private savedPredicates = '[]';
  private savedPreset = 'visitors-and-leads';
  private savedSmartSuggestionsEnabled = false;
  private savedSuggestionIds = '[]';

  static PRESET_PREDICATES: Record<string, Array<Record<string, unknown>>> = {
    'visitors-and-leads': [
      {
        attribute: 'role',
        type: 'role',
        comparison: 'in',
        value: ['visitor_role', 'contact_role'],
      },
    ],
    everyone: [
      {
        attribute: 'role',
        type: 'role',
        comparison: 'in',
        value: ['contact_role', 'user_role', 'visitor_role'],
      },
    ],
    'no-one': [],
  };

  get canUseMultibrand(): boolean {
    return this.appService.app.canUseFeature('messenger_multibrand');
  }

  get canUseSmartSuggestions(): boolean {
    return this.appService.app.canUseFeature('team-messenger-sales-messenger-smart-suggestions');
  }

  get effectiveSmartSuggestionsEnabled(): boolean {
    return this.canUseSmartSuggestions && this.smartSuggestionsEnabled;
  }

  get lookAndFeel() {
    return this.args.model.settings.lookAndFeel;
  }

  get brands() {
    return this.lookAndFeel.newBrands;
  }

  get brandSettings() {
    return this.lookAndFeel.brands;
  }

  get activeBrandSettingsId(): number | null {
    return this.activeBrandSetting?.id ?? null;
  }

  get isDirty(): boolean {
    if (!this.config) {
      return false;
    }
    if (this.glowColor !== this.savedGlowColor) {
      return true;
    }
    if (this.verticalPadding !== this.savedVerticalPadding) {
      return true;
    }
    // Toggle state changed (ON↔OFF)
    if (this.smartSuggestionsEnabled !== this.savedSmartSuggestionsEnabled) {
      return true;
    }
    // Suggestion order changed — only relevant when toggle is ON (OFF discards reorder)
    if (this.smartSuggestionsEnabled) {
      let currentIds = JSON.stringify(this.suggestions.map((s) => s.id));
      if (currentIds !== this.savedSuggestionIds) {
        return true;
      }
    }
    if (this.selectedPreset !== this.savedPreset) {
      return true;
    }
    if (this.selectedPreset === 'specific' && this.audienceRuleset) {
      let allPredicates = this.serializeAudienceRuleset();
      return JSON.stringify(allPredicates) !== this.savedPredicates;
    }
    return JSON.stringify(this.audiencePredicates) !== this.savedPredicates;
  }

  get isSaveDisabled(): boolean {
    return !this.isDirty || this.isSaving;
  }

  get glowColorOrUndefined(): string | undefined {
    return this.glowColor ?? undefined;
  }

  get verticalPaddingString(): string | undefined {
    return this.verticalPadding != null ? String(this.verticalPadding) : undefined;
  }

  get rulesEditorConfig() {
    return {
      editorConfiguration: {
        workflowMatchingTargetingAttributeIdentifiers: this.getAttributeIdentifiers(),
        workflowMatchingTargetingAttributes: this.getAttributeGroups(),
        supportsUserTargeting: true,
        supportsConversationChannelTargeting: false,
        targetingAttributePickerLabel: this.intl.t('messenger.sales.visibility.set-audience-rules'),
        targetingSectionTitle: '',
      },
      showAudiencePreview: false,
      hideAddRule: false,
    };
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.activeBrandSetting = this.resolveInitialBrand();
    this.messengerSettingsService.customSaveHandler = this.saveConfig;
    registerDestructor(this, () => {
      this.messengerSettingsService.customSaveHandler = null;
    });
    this.fetchConfig();
  }

  @action
  setBrandSetting(brandSetting: $TSFixMe) {
    this.activeBrandSetting = brandSetting;
    this.showAllSuggestions = false;
    this.updateStyleQueryParam(brandSetting);
    this.fetchConfig();
  }

  @action
  async fetchConfig() {
    if (!this.activeBrandSettingsId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    try {
      let appId = this.appService.app.id;
      let response = (await get(
        `/ember/messenger_settings/sales/${appId}?brand_settings_id=${this.activeBrandSettingsId}`,
      )) as SalesMessengerConfig;

      this.config = response;
      this.glowColor = response.glow_color;
      this.verticalPadding = response.vertical_padding ?? null;
      this.audiencePredicates = response.audience_predicates || [];
      this.smartSuggestionsEnabled = response.smart_suggestions_enabled ?? false;
      this.suggestions = response.suggestions ?? [];
      this.initAudienceRuleset(this.audiencePredicates);
      this.selectedPreset =
        this.audiencePredicates.length === 0 ? 'no-one' : this.detectPresetFromRuleset();
      this.snapshotSavedState();
    } catch (_e: unknown) {
      let defaultPredicates = SalesMessengerContainer.PRESET_PREDICATES['visitors-and-leads'];
      this.config = {
        brand_settings_id: this.activeBrandSettingsId,
        glow_color: null,
        vertical_padding: null,
        audience_predicates: defaultPredicates,
        smart_suggestions_enabled: false,
        suggestions: [],
      };
      this.glowColor = null;
      this.verticalPadding = null;
      this.audiencePredicates = defaultPredicates;
      this.smartSuggestionsEnabled = false;
      this.suggestions = [];
      this.selectedPreset = 'visitors-and-leads';
      this.initAudienceRuleset(defaultPredicates);
      this.snapshotSavedState();
    } finally {
      this.isLoading = false;
    }
  }

  @action
  selectPreset(preset: string) {
    this.selectedPreset = preset;
    if (preset !== 'specific') {
      let presetPredicates = SalesMessengerContainer.PRESET_PREDICATES[preset] || [];
      this.audiencePredicates = presetPredicates;
      this.initAudienceRuleset(presetPredicates);
    } else {
      // When switching to specific, init with empty ruleset — AudienceRulesEditor
      // auto-creates a default "Users" role predicate when supportsUserTargeting is true
      this.audiencePredicates = [];
      this.initAudienceRuleset([]);
    }
  }

  @action
  updateGlowColor(color: string) {
    this.glowColor = color;
  }

  @action
  updateVerticalPadding(event: Event) {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(value)) {
      this.verticalPadding = null;
      return;
    }
    if (value < MIN_VERTICAL_PADDING) {
      value = MIN_VERTICAL_PADDING;
      this.notificationsService.notifyError(
        this.intl.t(
          'messenger.look-and-feel-settings.customize.launcher.visibility.position.padding-validation-error',
        ),
      );
    }
    this.verticalPadding = value;
  }

  @action
  setOpenSection(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  async saveConfig() {
    if (!this.activeBrandSettingsId) {
      return;
    }

    this.isSaving = true;

    try {
      let predicatesToSave = this.audiencePredicates;
      if (this.selectedPreset === 'specific' && this.audienceRuleset) {
        predicatesToSave = this.serializeAudienceRuleset();
        this.audiencePredicates = predicatesToSave;
      }

      let appId = this.appService.app.id;
      let response = (await put(`/ember/messenger_settings/sales/${appId}`, {
        sales_messenger_configuration: {
          brand_settings_id: this.activeBrandSettingsId,
          glow_color: this.glowColor,
          vertical_padding: this.verticalPadding,
          audience_predicates: predicatesToSave,
          smart_suggestions_enabled: this.smartSuggestionsEnabled,
        },
      })) as SalesMessengerConfig;

      // Persist reorder if toggle is ON and order changed
      if (this.smartSuggestionsEnabled) {
        let currentIds = JSON.stringify(this.suggestions.map((s) => s.id));
        if (currentIds !== this.savedSuggestionIds) {
          await put(`/ember/messenger_settings/sales/${appId}/reorder_suggestions`, {
            suggestion_ids: this.suggestions.map((s) => s.id),
            brand_settings_id: this.activeBrandSettingsId,
          });
        }
      }

      this.config = response;
      this.glowColor = response.glow_color;
      this.verticalPadding = response.vertical_padding ?? null;
      this.audiencePredicates = response.audience_predicates || [];
      this.smartSuggestionsEnabled = response.smart_suggestions_enabled ?? false;
      this.suggestions = response.suggestions ?? [];
      this.initAudienceRuleset(this.audiencePredicates);
      this.selectedPreset =
        this.audiencePredicates.length === 0 ? 'no-one' : this.detectPresetFromRuleset();
      this.snapshotSavedState();
      this.notificationsService.notifyConfirmation(this.intl.t('messenger.sales.save-success'));
    } catch (_e: unknown) {
      this.notificationsService.notifyError(this.intl.t('messenger.sales.save-error'));
    } finally {
      this.isSaving = false;
    }
  }

  @action
  toggleSmartSuggestions() {
    this.smartSuggestionsEnabled = !this.smartSuggestionsEnabled;
    if (!this.smartSuggestionsEnabled) {
      // Revert suggestion order to saved state when toggling OFF
      let savedIds: number[] = JSON.parse(this.savedSuggestionIds);
      let suggestionMap = new Map(this.suggestions.map((s) => [s.id, s]));
      this.suggestions = savedIds
        .map((id) => suggestionMap.get(id))
        .filter((s): s is Suggestion => s !== undefined);
    }
  }

  @action
  reorderSuggestions(reorderedSuggestions: Suggestion[]) {
    // Mutate display_priority in place to preserve object identity for ember-sortable
    reorderedSuggestions.forEach((s, index) => {
      s.display_priority = index;
    });
    this.suggestions = [...reorderedSuggestions];
  }

  @action
  toggleShowAllSuggestions() {
    this.showAllSuggestions = !this.showAllSuggestions;
  }

  private resolveInitialBrand(): $TSFixMe {
    let styleParam = (this.router.currentRoute?.queryParams as Record<string, string>)?.style;
    if (styleParam) {
      let match = this.brandSettings.findBy('id', styleParam);
      if (match) {
        return match;
      }
    }
    return this.lookAndFeel.defaultBrand;
  }

  private updateStyleQueryParam(brandSetting: $TSFixMe) {
    let isDefault = brandSetting.isDefault;
    (this.router as $TSFixMe).replaceWith(this.router.currentRouteName, {
      queryParams: { style: isDefault ? null : brandSetting.id },
    });
  }

  private static USER_TYPE_TO_PRESET: Record<string, string> = {
    non_user: 'visitors-and-leads',
    everyone: 'everyone',
  };

  private detectPresetFromRuleset(): string {
    if (!this.audienceRuleset) {
      return 'no-one';
    }
    let roleGroup = this.audienceRuleset.get('rolePredicateGroup');
    let predicateGroup = this.audienceRuleset.get('predicateGroup');
    let hasCustomPredicates = predicateGroup?.get('hasPredicates');
    if (hasCustomPredicates) {
      return 'specific';
    }
    let userType = roleGroup?.get('userPredicate.userType');
    if (!userType) {
      return 'no-one';
    }
    return SalesMessengerContainer.USER_TYPE_TO_PRESET[userType] || 'specific';
  }

  private initAudienceRuleset(rawPredicates: Array<Record<string, unknown>>) {
    let allFragments = PredicateGroup.convertRawPredicates(this.store, rawPredicates);

    let roleFragments = allFragments.filter((f: $TSFixMe) => f.isUserPredicate);
    let otherFragments = allFragments.filter((f: $TSFixMe) => !f.isUserPredicate);

    if (roleFragments.length === 0) {
      let defaultRole = PredicateGroup.convertRawPredicates(this.store, [
        { attribute: 'role', type: 'role', comparison: 'in', value: ['user_role'] },
      ]);
      roleFragments = defaultRole;
    }

    let predicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: otherFragments,
    });

    let rolePredicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: roleFragments,
    });

    let clientPredicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: [],
    });

    let defaultPredicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: [],
    });

    this.audienceRuleset = EmberObject.create({
      predicateGroup,
      rolePredicateGroup,
      clientPredicateGroup,
      defaultPredicateGroup,
      fetchAudiencePreviewTask: EmberObject.create({
        perform: () => Promise.resolve({ count: 0 }),
        isRunning: false,
      }),
    });
  }

  private serializeAudienceRuleset(): Array<Record<string, unknown>> {
    if (!this.audienceRuleset) {
      return [];
    }
    let serialize = (p: $TSFixMe) => (p.serialize ? p.serialize() : p);

    // Use Ember's get() to read values set via Ember's set() by AudienceRulesEditor
    let rolePredicates = (this.audienceRuleset.get('rolePredicateGroup.predicates') || []).map(
      serialize,
    );
    let otherPredicates = (this.audienceRuleset.get('predicateGroup.predicates') || []).map(
      serialize,
    );

    return [...rolePredicates, ...otherPredicates];
  }

  private snapshotSavedState() {
    this.savedGlowColor = this.glowColor;
    this.savedVerticalPadding = this.verticalPadding;
    this.savedPredicates = JSON.stringify(this.audiencePredicates);
    this.savedPreset = this.selectedPreset;
    this.savedSmartSuggestionsEnabled = this.smartSuggestionsEnabled;
    this.savedSuggestionIds = JSON.stringify(this.suggestions.map((s) => s.id));
  }

  private getTargetingAttributes(): {
    personAttrs: $TSFixMe[];
    companyAttrs: $TSFixMe[];
  } {
    let userAttrs = this.attributeService.filterableUserAttributesForMessaging || [];
    let visitorAttrs = this.attributeService.visitorMessagingAttributes || [];
    let companyAttrs = this.attributeService.filterableCompanyAttributesForMessaging || [];
    return {
      personAttrs: [...userAttrs, ...visitorAttrs],
      companyAttrs,
    };
  }

  private getAttributeIdentifiers(): string[] {
    let { personAttrs, companyAttrs } = this.getTargetingAttributes();
    let validAttributes = this.appService.app?.companiesActive
      ? [...personAttrs, ...companyAttrs]
      : personAttrs;
    let identifiers = validAttributes
      .filter((attr: $TSFixMe) => attr?.identifier)
      .map((attr: $TSFixMe) => attr.identifier);
    return [...new Set([...identifiers, 'role'])];
  }

  private getAttributeGroups(): Array<{ heading: string; attributes: $TSFixMe[] }> {
    let { personAttrs, companyAttrs } = this.getTargetingAttributes();
    let groups = [
      {
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.person-data'),
        attributes: personAttrs,
      },
    ];
    if (this.appService.app?.companiesActive) {
      groups.push({
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.company-data'),
        attributes: companyAttrs,
      });
    }
    return groups;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Sales::Container': typeof SalesMessengerContainer;
  }
}
